import React, { useState } from 'react';

/**
 * Real product photos, rear view first — camera layout is what tells
 * iPhone 16 Pro Max from 17 Pro Max. Official Apple Store finish shots
 * lead with the back; GSMArena is the fallback.
 */

const APPLE_STORE = 'https://store.storeimages.cdn-apple.com/4982/as-images.apple.com/is';
const APPLEDB = 'https://img.appledb.dev/device@256';
const GSMARENA = 'https://fdn2.gsmarena.com/vv/bigpic';

const storeUrl = (id) => `${APPLE_STORE}/${id}?wid=400&fmt=png-alpha&qlt=90`;
const appleDbUrl = (id, color) => `${APPLEDB}/${id}/${String(color).replace(/ /g, '%20')}.png`;
const arenaUrl = (slug) => `${GSMARENA}/${slug}.jpg`;

/** marketing name → official back-facing store asset + fallbacks */
const APPLE_PHOTOS = {
  'iPhone 17 Pro Max': {
    store: 'iphone-17-pro-max-finish-select-cosmicorange-202509',
    crop: 'center',
    id: 'iPhone18,2',
    color: 'Cosmic Orange',
    year: 2025,
    slug: 'apple-iphone-17-pro-max'
  },
  'iPhone 17 Pro': {
    store: 'iphone-17-pro-finish-select-deepblue-202509',
    crop: 'center',
    id: 'iPhone18,1',
    color: 'Deep Blue',
    year: 2025,
    slug: 'apple-iphone-17-pro'
  },
  'iPhone 17': {
    store: 'iphone-17-finish-select-lavender-202509',
    crop: 'center',
    id: 'iPhone18,3',
    color: 'Lavender',
    year: 2025,
    slug: 'apple-iphone-17'
  },
  'iPhone 17e': {
    store: 'iphone-17e-finish-select-softpink-202603',
    crop: 'center',
    id: 'iPhone18,5',
    color: 'Soft Pink',
    year: 2026,
    slug: 'apple-iphone-17e'
  },
  'iPhone Air': {
    store: 'iphone-air-finish-select-skyblue-202509',
    crop: 'center',
    id: 'iPhone18,4',
    color: 'Sky Blue',
    year: 2025,
    slug: 'apple-iphone-air'
  },
  'iPhone 16 Pro Max': {
    store: 'iphone-16-pro-finish-select-202409-6-9inch-deserttitanium',
    crop: 'left',
    id: 'iPhone17,2',
    color: 'Desert Titanium',
    year: 2024,
    slug: 'apple-iphone-16-pro-max'
  },
  'iPhone 16 Pro': {
    store: 'iphone-16-pro-finish-select-202409-6-3inch-blacktitanium',
    crop: 'left',
    id: 'iPhone17,1',
    color: 'Black Titanium',
    year: 2024,
    slug: 'apple-iphone-16-pro'
  },
  'iPhone 16 Plus': {
    store: 'iphone-16-finish-select-202409-6-7inch-teal',
    crop: 'left',
    id: 'iPhone17,4',
    color: 'Teal',
    year: 2024,
    slug: 'apple-iphone-16-plus'
  },
  'iPhone 16': {
    store: 'iphone-16-finish-select-202409-6-1inch-ultramarine',
    crop: 'left',
    id: 'iPhone17,3',
    color: 'Ultramarine',
    year: 2024,
    slug: 'apple-iphone-16'
  },
  'iPhone 16e': { id: 'iPhone17,5', color: 'White', year: 2025, slug: 'apple-iphone-16e' },
  'iPhone 15 Pro Max': {
    store: 'iphone-15-pro-finish-select-202309-6-7inch-naturaltitanium',
    crop: 'left',
    id: 'iPhone16,2',
    color: 'Natural Titanium',
    year: 2023,
    slug: 'apple-iphone-15-pro-max'
  },
  'iPhone 15 Pro': {
    store: 'iphone-15-pro-finish-select-202309-6-1inch-blacktitanium',
    crop: 'left',
    id: 'iPhone16,1',
    color: 'Black Titanium',
    year: 2023,
    slug: 'apple-iphone-15-pro'
  },
  'iPhone 15 Plus': {
    store: 'iphone-15-finish-select-202309-6-7inch-pink',
    crop: 'left',
    id: 'iPhone15,5',
    color: 'Pink',
    year: 2023,
    slug: 'apple-iphone-15-plus'
  },
  'iPhone 15': {
    store: 'iphone-15-finish-select-202309-6-1inch-pink',
    crop: 'left',
    id: 'iPhone15,4',
    color: 'Pink',
    year: 2023,
    slug: 'apple-iphone-15'
  },
  'iPhone 14 Pro Max': {
    store: 'iphone-14-pro-finish-select-202209-6-7inch-deeppurple',
    crop: 'left',
    id: 'iPhone15,3',
    color: 'Deep Purple',
    year: 2022,
    slug: 'apple-iphone-14-pro-max'
  },
  'iPhone 14 Pro': {
    store: 'iphone-14-pro-finish-select-202209-6-1inch-deeppurple',
    crop: 'left',
    id: 'iPhone15,2',
    color: 'Deep Purple',
    year: 2022,
    slug: 'apple-iphone-14-pro'
  },
  'iPhone 14 Plus': {
    store: 'iphone-14-finish-select-202209-6-7inch-yellow',
    crop: 'left',
    id: 'iPhone14,8',
    color: 'Yellow',
    year: 2022,
    slug: 'apple-iphone-14-plus'
  },
  'iPhone 14': {
    store: 'iphone-14-finish-select-202209-6-1inch-midnight',
    crop: 'left',
    id: 'iPhone14,7',
    color: 'Midnight',
    year: 2022,
    slug: 'apple-iphone-14'
  },
  'iPhone 13 Pro Max': { id: 'iPhone14,3', color: 'Sierra Blue', year: 2021, slug: 'apple-iphone-13-pro-max' },
  'iPhone 13 Pro': { id: 'iPhone14,2', color: 'Sierra Blue', year: 2021, slug: 'apple-iphone-13-pro' },
  'iPhone 13 mini': { id: 'iPhone14,4', color: 'Pink', year: 2021, slug: 'apple-iphone-13-mini' },
  'iPhone 13': { id: 'iPhone14,5', color: 'Midnight', year: 2021, slug: 'apple-iphone-13' },
  'iPhone SE (3rd gen)': { id: 'iPhone14,6', color: '(PRODUCT)RED', year: 2022, slug: 'apple-iphone-se-2022' },
  'iPhone SE (2nd gen)': { id: 'iPhone12,8', color: '(PRODUCT)RED', year: 2020, slug: 'apple-iphone-se-2020' },
  'iPhone SE (1st gen)': { id: 'iPhone8,4', color: 'Space Gray', year: 2016, slug: 'apple-iphone-se' },
  'iPhone 12 Pro Max': { id: 'iPhone13,4', color: 'Pacific Blue', year: 2020, slug: 'apple-iphone-12-pro-max' },
  'iPhone 12 Pro': { id: 'iPhone13,3', color: 'Pacific Blue', year: 2020, slug: 'apple-iphone-12-pro' },
  'iPhone 12 mini': { id: 'iPhone13,1', color: 'Purple', year: 2020, slug: 'apple-iphone-12-mini' },
  'iPhone 12': { id: 'iPhone13,2', color: 'Purple', year: 2020, slug: 'apple-iphone-12' },
  'iPhone 11 Pro Max': { id: 'iPhone12,5', color: 'Midnight Green', year: 2019, slug: 'apple-iphone-11-pro-max' },
  'iPhone 11 Pro': { id: 'iPhone12,3', color: 'Midnight Green', year: 2019, slug: 'apple-iphone-11-pro' },
  'iPhone 11': { id: 'iPhone12,1', color: 'Purple', year: 2019, slug: 'apple-iphone-11' },
  'iPhone XR': { id: 'iPhone11,8', color: 'Coral', year: 2018, slug: 'apple-iphone-xr' },
  'iPhone XS Max': { id: 'iPhone11,6', color: 'Gold', year: 2018, slug: 'apple-iphone-xs-max' },
  'iPhone XS': { id: 'iPhone11,2', color: 'Gold', year: 2018, slug: 'apple-iphone-xs' },
  'iPhone X': { id: 'iPhone10,6', color: 'Silver', year: 2017, slug: 'apple-iphone-x' },
  'iPhone 8 Plus': { id: 'iPhone10,2', color: 'Space Gray', year: 2017, slug: 'apple-iphone-8-plus' },
  'iPhone 8': { id: 'iPhone10,1', color: 'Space Gray', year: 2017, slug: 'apple-iphone-8' },
  'iPhone 7 Plus': { id: 'iPhone9,2', color: 'Jet Black', year: 2016, slug: 'apple-iphone-7-plus' },
  'iPhone 7': { id: 'iPhone9,1', color: 'Jet Black', year: 2016, slug: 'apple-iphone-7' },
  'iPhone 6s Plus': { id: 'iPhone8,2', color: 'Rose Gold', year: 2015, slug: 'apple-iphone-6s-plus' },
  'iPhone 6s': { id: 'iPhone8,1', color: 'Rose Gold', year: 2015, slug: 'apple-iphone-6s' },
  'iPhone 6 Plus': { id: 'iPhone7,1', color: 'Space Gray', year: 2014, slug: 'apple-iphone-6-plus' },
  'iPhone 6': { id: 'iPhone7,2', color: 'Space Gray', year: 2014, slug: 'apple-iphone-6' },
  'iPhone 5s': { id: 'iPhone6,1', color: 'Space Gray', year: 2013, slug: 'apple-iphone-5s' }
};

const IPAD_PHOTOS = {
  'iPad Air': { id: 'iPad13,16', color: 'Blue', slug: 'apple-ipad-air-2022' },
  'iPad mini': { id: 'iPad14,1', color: 'Purple', slug: 'apple-ipad-mini-2021' },
  'iPad Pro': { id: 'iPad16,6', color: 'Silver', slug: 'apple-ipad-pro-13-2024' },
  iPad: { id: 'iPad13,18', color: 'Silver', slug: 'apple-ipad-10-2-2021' }
};

const cleanName = (raw) => {
  if (!raw) return '';
  return String(raw)
    .split(/\s+or\s+/)[0]
    .replace(/\s*\((SM-[A-Z0-9]+|iPhone\d+,\d+|iPad\d+,\d+|GT-[A-Z0-9]+)\)\s*$/i, '')
    .trim();
};

const lookupApple = (name) => {
  if (APPLE_PHOTOS[name]) return APPLE_PHOTOS[name];
  const key = Object.keys(APPLE_PHOTOS).find((k) => k.toLowerCase() === name.toLowerCase());
  return key ? APPLE_PHOTOS[key] : null;
};

const lookupIpad = (name) => {
  if (/mini/i.test(name)) return IPAD_PHOTOS['iPad mini'];
  if (/air/i.test(name)) return IPAD_PHOTOS['iPad Air'];
  if (/pro/i.test(name)) return IPAD_PHOTOS['iPad Pro'];
  return IPAD_PHOTOS.iPad;
};

const androidSlugs = (name) => {
  const lower = name.toLowerCase().replace(/[()]/g, '').replace(/\s+/g, ' ').trim();
  let titled = lower;
  if (/^galaxy\b/.test(titled)) titled = `samsung ${titled}`;
  if (/^pixel\b/.test(titled)) titled = `google ${titled}`;
  if (/^redmi|^poco|^mi /.test(titled)) titled = titled.startsWith('xiaomi') ? titled : `xiaomi ${titled}`;
  const slug = titled.replace(/\s+/g, '-').replace(/[^a-z0-9+-]/g, '');
  const slugs = [slug];
  if (!/-5g$/.test(slug)) slugs.push(`${slug}-5g`);
  if (slug.includes('ultra') && !slug.includes('samsung')) slugs.unshift(`samsung-${slug}`);
  return [...new Set(slugs)];
};

const appleImageUrls = (photo) => {
  const urls = [];
  if (photo.store) urls.push(storeUrl(photo.store));
  if (photo.slug) urls.push(arenaUrl(photo.slug));
  if (photo.id && photo.color) urls.push(appleDbUrl(photo.id, photo.color));
  return urls;
};

export const resolveDeviceVisual = (rawName = '') => {
  const name = cleanName(rawName);
  const lower = name.toLowerCase();

  const apple = lookupApple(name);
  if (apple) {
    return {
      displayName: name,
      color: apple.color,
      year: apple.year || null,
      crop: apple.crop || 'center',
      imageUrls: appleImageUrls(apple)
    };
  }

  if (/ipad/.test(lower)) {
    const ipad = lookupIpad(name);
    return {
      displayName: name || 'iPad',
      color: ipad.color,
      year: null,
      crop: 'center',
      imageUrls: appleImageUrls(ipad)
    };
  }

  if (name && !/mac|windows|linux|desktop|computer|smarttv|^tv$/.test(lower)) {
    return {
      displayName: name,
      color: null,
      year: null,
      crop: 'center',
      imageUrls: androidSlugs(name).map(arenaUrl)
    };
  }

  return { displayName: name || 'Device', color: null, year: null, crop: 'center', imageUrls: [] };
};

const DeviceMockup = ({ name, size = 112, showLabel = true, variant = 'card' }) => {
  const spec = resolveDeviceVisual(name);
  const [srcIndex, setSrcIndex] = useState(0);
  const src = spec.imageUrls[srcIndex] || null;
  const hero = variant === 'hero';
  const titleClass = hero ? 'text-white' : 'text-gray-900 dark:text-white';
  const subClass = hero ? 'text-white/70' : 'text-gray-500 dark:text-gray-400';

  return (
    <div className="flex flex-col items-center" aria-label={spec.displayName || name}>
      <div className="flex items-center justify-center" style={{ width: size, minHeight: Math.round(size * 0.9) }}>
        {src ? (
          <img
            src={src}
            alt={`${spec.displayName} back`}
            width={size}
            className="object-contain drop-shadow-md"
            style={{ width: size, height: 'auto', maxHeight: Math.round(size * 1.15) }}
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setSrcIndex((i) => i + 1)}
          />
        ) : (
          <div
            className={`rounded-xl ${hero ? 'bg-white/20' : 'bg-gray-200 dark:bg-gray-700'}`}
            style={{ width: size * 0.55, height: size * 0.9 }}
          />
        )}
      </div>
      {showLabel && (
        <div className="mt-1.5 text-center px-0.5 max-w-[9.5rem]">
          <p className={`text-[11px] font-semibold leading-tight ${titleClass}`}>{spec.displayName}</p>
          {(spec.color || spec.year) && (
            <p className={`text-[10px] leading-tight mt-0.5 ${subClass}`}>
              {[spec.color, spec.year].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default DeviceMockup;
